import { trackError } from '@vend/utilities'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { ClientApiSaleData } from '../../../../factories/sale-transformer'
import {
  isPaymentStep,
  validateEventOrigin,
} from '../../../../functions/integrated-payment'
import { usePaymentManager } from '../../../../ng-compat/hooks'
import { TRANSACTION_STATUSES } from '../../swiper-payment-modal/swiper-payment-modal-content.constant'
import {
  AcceptStep,
  PaymentResponseData,
  PaymentStepEvent,
  PaymentStepPrintSignatureSlip,
  PaymentStepSetup,
  PAYMENT_STEPS,
  RejectStep,
} from '../integrated-payment-steps.constant'
import { PaymentData } from './IntegratedPaymentModal'
import { preloadSignatureImage } from './integratedPaymentModalHelpers'

interface GatewayConfig {
  isFullscreen: boolean
  enableClose: boolean
  header?: string
}

/**
 * React hook which attaches an event listener to the window that listens for messages from a payment integrator.
 *
 * @param {
 *  gatewaySrc: The url of the gateway
 *  paymentData: Data related to the payment
 *  saleData:  Data related to the sale
 *  iframeId: The id of the iframe element the gateway is loaded in. This is required to find and send messages back to the gateway.
 *  onAccept: A callback which is called when the payment flow is accepted
 *  onReject: A callback which is called when the payment is rejected
 * }
 *
 * @returns GatewayConfig - some state which is updated based on messages received from the gateway.
 */
export const useGatewayHandler = ({
  gatewaySrc,
  paymentData,
  saleData,
  iframeId,
  onAccept,
  onReject,
}: {
  gatewaySrc: string
  paymentData: PaymentData
  saleData: ClientApiSaleData
  iframeId: string
  onAccept: (data: AcceptStep) => void
  onReject: (data: RejectStep) => void
}): GatewayConfig => {
  const paymentManager = usePaymentManager()

  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    paymentData.paymentType.config?.display_gateway_fullscreen || false
  )
  const [enableClose, setEnableClose] = useState<boolean>(true)
  const [header, setHeader] = useState<string>()

  useEffect(() => {
    if (isFullscreen) {
      setEnableClose(false)
    }
  }, [isFullscreen])

  /**
   * Posts passed data to the gateway.
   *
   * @param  event
   *         The post message event from the payment gateway.
   */
  const dataStepHandler = useCallback(
    (
      event: MessageEvent,
      paymentData: PaymentData,
      saleData: ClientApiSaleData
    ): void => {
      const gateway = (document.getElementById(iframeId) as HTMLIFrameElement)
        .contentWindow
      const data: PaymentResponseData = {
        payment: {
          amount: paymentData.amount,
          external_transaction_id: paymentData.externalTransactionIds,
          register_sale_payment_id: paymentData.registerSalePaymentIds,
          reference_id: paymentData.paymentId,
          register_id: paymentData.registerId,
        },
        register_sale: saleData,
        step: PAYMENT_STEPS.DATA,
        success: true,
      }

      gateway!.postMessage(JSON.stringify(data), event.origin)
    },
    [iframeId]
  )

  /**
   * Print a signature slip receipt and accept the transaction with APPROVED_SIGNATURE status
   * It will preload the signature image first if necessary
   *
   * @param eventData
   *        The post message received from the payment gateway.
   */
  const printSignatureSlipHandler = useCallback(
    (
      eventData: PaymentStepPrintSignatureSlip,
      paymentData: PaymentData
    ): void => {
      preloadSignatureImage(eventData.signature_url).then(imageSrc => {
        paymentManager.emit('printReceipt', {
          isSignatureSlipReceipt: true,
          paymentType: paymentData.paymentType,
          transaction: {
            tenders: [
              {
                amount: paymentData.amount,
                card_details: {
                  card: {
                    last_four: eventData.card_last_four,
                  },
                },
                created_at: eventData.payment_date
                  ? eventData.payment_date
                  : moment.utc().format(),
                external_transaction_id: eventData.external_transaction_id,
                provider_fields: {
                  signature_base64: eventData.signature_base64
                    ? 'data:image/png;base64,' + eventData.signature_base64
                    : null,
                  signature_url: imageSrc,
                  terminal_id: eventData.terminal_serial_number,
                },
              },
            ],
          },
        })

        onAccept({
          ...eventData,
          transaction_status: TRANSACTION_STATUSES.APPROVED_SIGNATURE,
          signature_url: imageSrc || undefined,
        })
      })
    },
    [onAccept, paymentManager]
  )

  /**
   * Invokes the appropriate action based on the step passed.
   *
   * @param  event
   *         The post message event from the payment gateway.
   * @param  eventData
   *         The parsed and validated PaymentStepEvent.
   */
  const stepEventHandler = useCallback(
    (
      event: MessageEvent,
      eventData: PaymentStepEvent,
      paymentData: PaymentData,
      saleData: ClientApiSaleData
    ): void => {
      const { step } = eventData

      switch (step) {
        case PAYMENT_STEPS.SETUP:
          setupStepHandler(eventData)
          break

        case PAYMENT_STEPS.DATA:
          dataStepHandler(event, paymentData, saleData)
          break

        case PAYMENT_STEPS.PRINT:
          paymentManager.emit('printReceipt', {
            receiptHtmlExtra: eventData.receipt_html_extra,
          })
          break

        case PAYMENT_STEPS.ACCEPT_SIGNATURE:
          printSignatureSlipHandler(eventData, paymentData)
          break

        case PAYMENT_STEPS.ACCEPT:
          onAccept(eventData)
          break

        case PAYMENT_STEPS.DECLINE:
        case PAYMENT_STEPS.EXIT:
          onReject(eventData)
          break
        default: {
          // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
          const _exhaustiveCheck: never = eventData
          return _exhaustiveCheck
        }
      }
    },
    [
      dataStepHandler,
      onAccept,
      onReject,
      paymentManager,
      printSignatureSlipHandler,
    ]
  )

  /**
   * Handles the post message step.
   *
   * @param  event
   *         The gateway post message event.
   */
  const gatewayMessageHandler = useCallback(
    (event: MessageEvent): void => {
      if (
        !event ||
        !event.data ||
        // comparing event.source to current window lets us ignore messages from our own client API
        event.source === window
      ) {
        return
      }

      const { paymentType } = paymentData
      const failedEventData = {
        event,
        retailerPaymentType: {
          id: paymentType.id,
          paymentTypeId:
            paymentType.payment_type && paymentType.payment_type.id,
        },
      }

      let eventData: PaymentStepEvent
      try {
        eventData = JSON.parse(event.data)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          '[IntegratedPaymentModal] Error parsing gateway message',
          failedEventData
        )
        return
      }

      if (!isPaymentStep(eventData.step)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[IntegratedPaymentModal] Unrecognized gateway message received',
          failedEventData
        )
        return
      }

      // eslint-disable-next-line no-console
      console.info(
        '[IntegratedPaymentModal] Gateway message received',
        eventData
      )

      if (
        !validateEventOrigin(event.origin, gatewaySrc, window.location.href)
      ) {
        trackError('[GatewayHandler] invalid payment message event origin', {
          data: {
            isReact: true,
            paymentType,
            gatewaySrc,
            eventData: event.data,
            eventOrigin: event.origin,
            eventSource: event.source,
          },
        })
        return
      }

      stepEventHandler(event, eventData, paymentData, saleData)
    },
    [gatewaySrc, paymentData, saleData, stepEventHandler]
  )

  /**
   * Sets the heading and/or disables the close button on the modal.
   *
   * @param  eventData
   *         The post message event data from the payment gateway.
   */
  const setupStepHandler = (eventData: PaymentStepSetup): void => {
    const { setup } = eventData

    if (setup.display_gateway_fullscreen !== undefined) {
      setIsFullscreen(setup.display_gateway_fullscreen)
    }

    if (setup.enable_close !== undefined) {
      setEnableClose(setup.enable_close)
    }

    if (setup.heading) {
      setHeader(setup.heading)
    }
  }

  // attach the gateway messageHandler
  useEffect(() => {
    window.addEventListener('message', gatewayMessageHandler)

    // ensure we clean up all event listeners we attached
    return () => {
      window.removeEventListener('message', gatewayMessageHandler)
    }
  }, [gatewayMessageHandler])

  return {
    enableClose,
    isFullscreen,
    header,
  }
}
